import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFileImport,
  faPlus,
  faShieldHalved,
  faGripVertical,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import WhiteMainBackground from "../../../shared_components/WhiteMainBackground/WhiteMainBackground";
import StyledButton from "../../../shared_components/StyledButton/StyledButton";
import InfoPage from "../../InfoPage/InfoPage";
import UserData from "../../../services/UserData";
import Permissions from "../../../services/Permissions";
import QAProcessAdminService from "../../../services/QAProcessAdminService";
import CustomToast from "../../../shared_components/CustomToast/CustomToast";
import style from "./QaAdminSetup.module.css";

const createNewHead = (id) => ({
  id,
  title: "NEW ITEM HEAD",
  lastSyncedTitle: null,
  items: [],
  isExpanded: false,
  isSaving: false,
  savedTemplateId: null,
  isDirty: true,
});

const QaAdminSetup = () => {
  const navigate = useNavigate();
  const userData = useContext(UserData);
  const [itemHeads, setItemHeads] = useState([]);
  const [isLoadingHeads, setIsLoadingHeads] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [deleteBusyKey, setDeleteBusyKey] = useState(null);
  const [confirmDeleteHeadId, setConfirmDeleteHeadId] = useState(null);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [isImportingDefaults, setIsImportingDefaults] = useState(false);

  const isAdmin = useMemo(() => Permissions.isAdmin(userData), [userData]);

  useLayoutEffect(() => {
    if (!isAdmin || !userData?.jwtToken) {
      return;
    }
    const cached = QAProcessAdminService.peekCachedQaAdminHeads(userData);
    if (cached !== null) {
      setItemHeads(cached);
      setIsLoadingHeads(false);
    } else {
      setIsLoadingHeads(true);
    }
  }, [isAdmin, userData, userData?.jwtToken]);

  useEffect(() => {
    if (!isAdmin || !userData?.jwtToken) {
      return undefined;
    }
    let cancelled = false;

    QAProcessAdminService.loadQaItemHeadsFromApi(userData)
      .then((heads) => {
        if (!cancelled) {
          setItemHeads(heads);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setToastMessage(error?.message || "Unable to load QA item heads.");
          setShowToast(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingHeads(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, userData, userData?.jwtToken]);

  useEffect(() => {
    if (confirmDeleteHeadId == null) {
      return undefined;
    }
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setConfirmDeleteHeadId(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [confirmDeleteHeadId]);

  useEffect(() => {
    if (!confirmImportOpen) {
      return undefined;
    }
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setConfirmImportOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [confirmImportOpen]);

  const goBack = () => {
    navigate("/qa-process");
  };

  const addItemHead = () => {
    setItemHeads((current) => {
      const next = [...current, createNewHead(Date.now())];
      QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
      return next;
    });
  };

  const requestDeleteItemHead = (headId) => {
    if (deleteBusyKey) {
      return;
    }
    setConfirmDeleteHeadId(headId);
  };

  const cancelDeleteItemHead = () => {
    setConfirmDeleteHeadId(null);
  };

  const confirmDeleteItemHeadAction = async () => {
    if (confirmDeleteHeadId == null) {
      return;
    }
    const headId = confirmDeleteHeadId;
    setConfirmDeleteHeadId(null);
    await deleteItemHead(headId);
  };

  const deleteItemHead = async (headId) => {
    const head = itemHeads.find((h) => h.id === headId);
    if (!head) {
      return;
    }
    const busyKey = `head:${headId}`;
    if (deleteBusyKey) {
      return;
    }

    if (head.savedTemplateId) {
      setDeleteBusyKey(busyKey);
      try {
        await QAProcessAdminService.deleteTemplate(userData, String(head.savedTemplateId));
        setItemHeads((current) => {
          const next = current.filter((h) => h.id !== headId);
          QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
          return next;
        });
        showUserToast("Item head deleted.");
      } catch (error) {
        showUserToast(error?.message || "Unable to delete item head.");
      } finally {
        setDeleteBusyKey(null);
      }
      return;
    }

    setItemHeads((current) => {
      const next = current.filter((h) => h.id !== headId);
      QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
      return next;
    });
  };

  const toggleItemHead = (headId) => {
    setItemHeads((current) =>
      current.map((head) =>
        head.id === headId ? { ...head, isExpanded: !head.isExpanded } : head
      )
    );
  };

  const addSubItem = (headId) => {
    setItemHeads((current) => {
      const next = current.map((head) =>
        head.id === headId
          ? {
              ...head,
              items: [
                ...head.items,
                {
                  id: `${Date.now()}-${Math.random()}`,
                  savedItemId: null,
                  text: "New Sub-item",
                },
              ],
              isDirty: true,
            }
          : head
      );
      QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
      return next;
    });
  };

  const updateItemHeadTitle = (headId, title) => {
    setItemHeads((current) =>
      current.map((head) =>
        head.id === headId ? { ...head, title, isDirty: true } : head
      )
    );
  };

  const updateSubItemText = (headId, itemId, text) => {
    setItemHeads((current) =>
      current.map((head) =>
        head.id === headId
          ? {
              ...head,
              items: head.items.map((item) =>
                item.id === itemId ? { ...item, text } : item
              ),
              isDirty: true,
            }
          : head
      )
    );
  };

  const deleteSubItem = async (headId, itemId) => {
    const head = itemHeads.find((h) => h.id === headId);
    const item = head?.items?.find((i) => i.id === itemId);
    if (!head || !item) {
      return;
    }
    const busyKey = `item:${headId}:${itemId}`;
    if (deleteBusyKey) {
      return;
    }

    const templateId = head.savedTemplateId ? String(head.savedTemplateId) : null;
    const persistedItemId = item.savedItemId ? String(item.savedItemId) : null;

    if (templateId && persistedItemId) {
      setDeleteBusyKey(busyKey);
      try {
        await QAProcessAdminService.deleteTemplateItem(userData, templateId, persistedItemId);
        setItemHeads((current) => {
          const next = current.map((h) =>
            h.id === headId
              ? {
                  ...h,
                  items: h.items.filter((i) => i.id !== itemId),
                  isDirty: false,
                }
              : h
          );
          QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
          return next;
        });
      } catch (error) {
        showUserToast(error?.message || "Unable to delete sub-item.");
      } finally {
        setDeleteBusyKey(null);
      }
      return;
    }

    setItemHeads((current) => {
      const next = current.map((h) =>
        h.id === headId
          ? {
              ...h,
              items: h.items.filter((i) => i.id !== itemId),
              isDirty: true,
            }
          : h
      );
      QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
      return next;
    });
  };

  const showUserToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const requestImportDefaults = () => {
    if (isImportingDefaults || deleteBusyKey) {
      return;
    }
    setConfirmImportOpen(true);
  };

  const cancelImportDefaults = () => {
    setConfirmImportOpen(false);
  };

  const runImportDefaults = async () => {
    if (!userData?.jwtToken || isImportingDefaults) {
      return;
    }
    setConfirmImportOpen(false);
    setIsImportingDefaults(true);
    try {
      const summary = await QAProcessAdminService.importBuiltInDefaultQaAdminChecklists(userData);
      const heads = await QAProcessAdminService.loadQaItemHeadsFromApi(userData);
      setItemHeads(heads);
      const { templatesCreated, sectionsSkipped, itemsCreated } = summary;
      if (templatesCreated === 0 && itemsCreated === 0) {
        showUserToast(
          sectionsSkipped > 0
            ? `No new templates created (${sectionsSkipped} section(s) already existed).`
            : "Nothing to import."
        );
      } else {
        const skippedPart =
          sectionsSkipped > 0 ? ` ${sectionsSkipped} existing section(s) skipped.` : "";
        showUserToast(
          `Imported ${templatesCreated} template(s) and ${itemsCreated} checklist line(s).${skippedPart}`
        );
      }
    } catch (error) {
      showUserToast(error?.message || "Unable to import default checklists.");
    } finally {
      setIsImportingDefaults(false);
    }
  };

  const saveItemHead = async (headId) => {
    const currentHead = itemHeads.find((head) => head.id === headId);
    if (!currentHead) {
      return;
    }

    const title = currentHead.title.trim();
    if (!title) {
      showUserToast("Please enter an item head title before saving.");
      return;
    }

    setItemHeads((current) =>
      current.map((head) =>
        head.id === headId ? { ...head, isSaving: true } : head
      )
    );

    try {
      const response = await QAProcessAdminService.saveItemHeadBlock(userData, {
        ...currentHead,
        title,
      });
      setItemHeads((current) => {
        const next = current.map((head) =>
          head.id === headId
            ? {
                ...head,
                id: String(response.templateId),
                title,
                lastSyncedTitle: title,
                items:
                  Array.isArray(response.items) && response.items.length > 0
                    ? response.items
                    : head.items,
                isSaving: false,
                savedTemplateId: String(response.templateId),
                isDirty: false,
              }
            : head
        );
        QAProcessAdminService.commitQaAdminHeadsCache(userData, next);
        return next;
      });
      showUserToast("Item head saved successfully.");
    } catch (error) {
      setItemHeads((current) =>
        current.map((head) =>
          head.id === headId ? { ...head, isSaving: false } : head
        )
      );
      showUserToast(error.message || "Unable to save item head.");
    }
  };

  if (!isAdmin) {
    return (
      <WhiteMainBackground>
        <InfoPage
          reason="Access denied"
          description="Only admins can access QA Admin Setup."
        />
      </WhiteMainBackground>
    );
  }

  return (
    <WhiteMainBackground className={style.main}>
      <div className={style.header_row}>
        <div>
          <div className={style.heading}>
            <FontAwesomeIcon
              icon={faArrowLeft}
              className={style.back_button}
              onClick={goBack}
            />
            <h1>QA ADMIN SETUP</h1>
          </div>
          <p className={style.subheading}>
            Configure global process headers and item groups
          </p>
        </div>
        <div className={style.header_actions}>
          <StyledButton
            className={style.secondary_button}
            clickEvent={requestImportDefaults}
            disabled={isLoadingHeads || isImportingDefaults}
          >
            <FontAwesomeIcon icon={faFileImport} />
            {isImportingDefaults ? "IMPORTING…" : "IMPORT DEFAULTS"}
          </StyledButton>
          <StyledButton
            className={style.primary_button}
            clickEvent={addItemHead}
            disabled={isLoadingHeads || isImportingDefaults}
          >
            <FontAwesomeIcon icon={faPlus} />
            ADD ITEM HEAD
          </StyledButton>
        </div>
      </div>

      {isLoadingHeads ? (
        <div className={style.empty_state}>
          <p className={style.loading_message}>Loading saved item heads…</p>
        </div>
      ) : itemHeads.length === 0 ? (
        <div className={style.empty_state}>
          <div className={style.empty_icon_wrap}>
            <FontAwesomeIcon icon={faShieldHalved} className={style.empty_icon} />
          </div>
          <h2>NO ITEM HEADS CONFIGURED</h2>
          <p>Start by adding a new top-level category for your QA processes.</p>
          <div className={style.empty_actions}>
            <StyledButton
              className={style.secondary_button}
              clickEvent={requestImportDefaults}
              disabled={isImportingDefaults}
            >
              <FontAwesomeIcon icon={faFileImport} />
              {isImportingDefaults ? "IMPORTING…" : "IMPORT DEFAULT CHECKLISTS"}
            </StyledButton>
            <StyledButton
              className={style.primary_button}
              clickEvent={addItemHead}
              disabled={isImportingDefaults}
            >
              ADD YOUR FIRST HEADER
            </StyledButton>
          </div>
        </div>
      ) : (
        <div className={style.heads_list}>
          {itemHeads.map((head) => (
            <div key={head.id} className={style.head_card}>
              <div className={style.head_card_header}>
                <div className={style.head_title}>
                  <button
                    type="button"
                    className={style.toggle_button}
                    onClick={() => toggleItemHead(head.id)}
                    aria-label={head.isExpanded ? "Collapse sub-items" : "Expand sub-items"}
                  >
                    <FontAwesomeIcon
                      icon={faGripVertical}
                      className={`${style.grip_icon} ${head.isExpanded ? style.grip_expanded : style.grip_collapsed}`}
                    />
                  </button>
                  <input
                    type="text"
                    value={head.title}
                    onChange={(event) => updateItemHeadTitle(head.id, event.target.value)}
                    className={style.head_title_input}
                    aria-label="Item head title"
                  />
                </div>
                <div className={style.head_actions}>
                  <StyledButton
                    className={style.secondary_button}
                    clickEvent={() => addSubItem(head.id)}
                    disabled={isImportingDefaults}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    ADD ITEM
                  </StyledButton>
                  <StyledButton
                    className={style.save_button}
                    clickEvent={() => saveItemHead(head.id)}
                    disabled={head.isSaving || isImportingDefaults}
                  >
                    {head.isSaving ? "SAVING..." : "SAVE"}
                  </StyledButton>
                  {!head.isDirty && head.savedTemplateId ? (
                    <span className={style.saved_badge}>Saved</span>
                  ) : null}
                  <button
                    type="button"
                    className={style.delete_button}
                    onClick={() => requestDeleteItemHead(head.id)}
                    disabled={Boolean(deleteBusyKey) || head.isSaving || isImportingDefaults}
                    aria-label="Delete item head"
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </div>
              {head.isExpanded ? (
                <div className={style.head_card_body}>
                  {head.items.length === 0 ? (
                    <p className={style.empty_items}>
                      No items added yet. Click &quot;Add Item&quot; to begin.
                    </p>
                  ) : (
                    <div className={style.items_row}>
                      {head.items.map((item) => (
                        <div key={item.id} className={style.item_chip}>
                          <span className={style.item_dot} />
                          <input
                            type="text"
                            value={item.text}
                            onChange={(event) =>
                              updateSubItemText(head.id, item.id, event.target.value)
                            }
                            className={style.sub_item_input}
                            aria-label="Sub-item name"
                          />
                          <button
                            type="button"
                            className={style.sub_item_delete_button}
                            onClick={() => deleteSubItem(head.id, item.id)}
                            disabled={Boolean(deleteBusyKey) || head.isSaving || isImportingDefaults}
                            aria-label="Delete sub-item"
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {confirmImportOpen ? (
        <div
          className={style.confirm_overlay}
          role="presentation"
          onClick={cancelImportDefaults}
        >
          <div
            className={style.confirm_dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qa-admin-import-defaults-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="qa-admin-import-defaults-title" className={style.confirm_title}>
              Import default checklists?
            </h2>
            <p className={style.confirm_body}>
              This creates the standard sections (BRIEFING, AUDIO, DESIGN, CODING) and all built-in
              checklist lines via the same APIs as manual setup. Sections that already exist are
              skipped.
              {itemHeads.length > 0 ? (
                <>
                  {" "}
                  You currently have <strong>{itemHeads.length}</strong> heading
                  {itemHeads.length === 1 ? "" : "s"} configured; existing templates are not removed.
                </>
              ) : null}
            </p>
            <div className={style.confirm_actions}>
              <StyledButton className={style.secondary_button} clickEvent={cancelImportDefaults}>
                Cancel
              </StyledButton>
              <StyledButton
                className={style.primary_button}
                clickEvent={() => {
                  void runImportDefaults();
                }}
                disabled={isImportingDefaults}
              >
                Import
              </StyledButton>
            </div>
          </div>
        </div>
      ) : null}
      {confirmDeleteHeadId != null ? (
        <div
          className={style.confirm_overlay}
          role="presentation"
          onClick={cancelDeleteItemHead}
        >
          <div
            className={style.confirm_dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qa-admin-delete-heading-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="qa-admin-delete-heading-title" className={style.confirm_title}>
              Delete this heading?
            </h2>
            <p className={style.confirm_body}>
              {(() => {
                const pending = itemHeads.find((h) => h.id === confirmDeleteHeadId);
                const label = (pending?.title || "").trim() || "Untitled";
                if (pending?.savedTemplateId) {
                  return (
                    <>
                      <strong>{label}</strong> will be removed from the server, including all of its
                      sub-items. This cannot be undone.
                    </>
                  );
                }
                return (
                  <>
                    Remove <strong>{label}</strong> from this screen? This heading has not been saved
                    yet.
                  </>
                );
              })()}
            </p>
            <div className={style.confirm_actions}>
              <StyledButton className={style.secondary_button} clickEvent={cancelDeleteItemHead}>
                Cancel
              </StyledButton>
              <StyledButton
                className={style.confirm_danger_button}
                clickEvent={() => {
                  void confirmDeleteItemHeadAction();
                }}
                disabled={Boolean(deleteBusyKey)}
              >
                Delete
              </StyledButton>
            </div>
          </div>
        </div>
      ) : null}
      <CustomToast message={toastMessage} showToast={showToast} setShow={setShowToast} />
    </WhiteMainBackground>
  );
};

export default QaAdminSetup;
